import { apiClient } from "@/lib/api";
import type { StandardResponse } from "@/types/api-response";
import { extractData } from "@/types/api-response";

/** Slugs the backend accepts; mirrors AgreementRegistry.Slugs. */
export const AGREEMENT_TYPES = [
  "signup_commitment",
  "vm_creation_commitment",
] as const;
export type AgreementType = (typeof AGREEMENT_TYPES)[number];

export const AGREEMENT_TYPE_LABELS: Record<AgreementType, string> = {
  signup_commitment: "After sign-up",
  vm_creation_commitment: "Before creating a machine",
};

/**
 * Sentinel for "no commitment filter" on the tenant and user lists. Radix Select rejects
 * `<SelectItem value="">`, so the cleared state needs a real value.
 */
export const ALL_AGREEMENTS = "all";

/** One choice of the commitment filter: a commitment and whether it was agreed to. */
export type AgreementFilterValue =
  | typeof ALL_AGREEMENTS
  | `${AgreementType}:accepted`
  | `${AgreementType}:outstanding`;

export const AGREEMENT_FILTER_OPTIONS: {
  value: AgreementFilterValue;
  label: string;
}[] = [
  { value: ALL_AGREEMENTS, label: "All Commitments" },
  ...AGREEMENT_TYPES.flatMap((type) => [
    {
      value: `${type}:accepted` as const,
      label: `${AGREEMENT_TYPE_LABELS[type]}: agreed`,
    },
    {
      value: `${type}:outstanding` as const,
      label: `${AGREEMENT_TYPE_LABELS[type]}: not agreed`,
    },
  ]),
];

/**
 * Splits a filter choice into the commitment slug and the accepted flag the list endpoints take,
 * or returns undefined for "all" and for anything no longer offered.
 */
export function parseAgreementFilter(
  value: string,
): { type: AgreementType; accepted: boolean } | undefined {
  const isOffered = AGREEMENT_FILTER_OPTIONS.some(
    (option) => option.value === value,
  );
  if (!isOffered || value === ALL_AGREEMENTS) {
    return undefined;
  }

  const [type, state] = value.split(":");
  return { type: type as AgreementType, accepted: state === "accepted" };
}

/** Label of a filter choice, for the active-filter chip. */
export function agreementFilterLabel(value: string): string {
  return (
    AGREEMENT_FILTER_OPTIONS.find((option) => option.value === value)?.label ??
    value
  );
}

export const AGREEMENT_TYPE_HINTS: Record<AgreementType, string> = {
  signup_commitment:
    "Shown the first time a tenant opens the console. They cannot use it until they accept.",
  vm_creation_commitment:
    "Shown before the first machine is ordered. Enforced in the API, not only in the UI.",
};

/** What revoking one commitment removed. Mirrors AgreementRevocationDto. */
export interface AgreementRevocationDto {
  type: string;
  tenantsRevoked: number;
  usersRevoked: number;
}

/** What one run of the agreement backfill did. Mirrors AgreementBackfillDto. */
export interface AgreementBackfillDto {
  /** Tenants whose settings still hold legacy acceptances; the same on every run. */
  scannedCount: number;
  /** Tenants given at least one acceptance by this run. */
  updatedTenantCount: number;
  /** Users given at least one acceptance by this run. */
  updatedUserCount: number;
}

/** Supported languages, in the order the editor shows them. The first is mandatory. */
export const AGREEMENT_CULTURES = ["vi", "en"] as const;
export type AgreementCulture = (typeof AGREEMENT_CULTURES)[number];

export const AGREEMENT_CULTURE_LABELS: Record<string, string> = {
  vi: "Tiếng Việt",
  en: "English",
};

export interface AgreementTranslationDto {
  culture: string;
  title: string;
  /** Already reduced to the supported tags by the server, so this is what customers read. */
  bodyHtml: string;
}

export interface AgreementRevisionDto {
  id: string;
  type: AgreementType;
  version: number;
  /** Oldest revision that still counts as agreed. Lower than `version` for a correction. */
  minAcceptedVersion: number;
  /** Every language this revision was published in; "vi" is always present. */
  translations: AgreementTranslationDto[];
  /** Covers every language, so it fingerprints the revision rather than one rendering. */
  contentHash: string;
  isActive: boolean;
  /** True for the revision customers are being shown — the highest version of its commitment. */
  isCurrent: boolean;
  publishedAt: string;
  /** Publish responses only: true when unsupported markup was stripped before saving. */
  contentWasReduced?: boolean;
}

export interface PublishAgreementRequest {
  type: AgreementType;
  /**
   * One entry per language. "vi" is mandatory — everything downstream falls back to it, so a
   * revision without it would leave a customer with an empty dialog and no way past the gate.
   */
  translations: AgreementTranslationDto[];
  /** False publishes a correction: a new revision that does not ask agreed people again. */
  requireReacceptance: boolean;
}

export const adminAgreementService = {
  async getAgreements(): Promise<AgreementRevisionDto[]> {
    const response = await apiClient.get<
      StandardResponse<AgreementRevisionDto[]>
    >("/api/v1/admin/agreements");
    return extractData(response.data);
  },

  async publish(data: PublishAgreementRequest): Promise<AgreementRevisionDto> {
    const response = await apiClient.post<
      StandardResponse<AgreementRevisionDto>
    >("/api/v1/admin/agreements", data);
    return extractData(response.data);
  },

  async setActive(
    type: AgreementType,
    isActive: boolean,
  ): Promise<AgreementRevisionDto> {
    const response = await apiClient.put<
      StandardResponse<AgreementRevisionDto>
    >("/api/v1/admin/agreements/active", { type, isActive });
    return extractData(response.data);
  },

  /**
   * Copy the acceptances kept in tenant settings into the tenant and user agreement columns
   * (SuperAdmin only). A revision already recorded is skipped, so calling it again changes nothing.
   */
  async backfill(): Promise<AgreementBackfillDto> {
    const response = await apiClient.post<
      StandardResponse<AgreementBackfillDto>
    >("/api/v1/admin/agreements/backfill");
    return extractData(response.data);
  },
};
